import React, { useEffect, useRef } from 'react';

interface VoiceVisualizerProps {
  amplitude: number;
  isActive: boolean;
  status: string;
}

const VoiceVisualizer: React.FC<VoiceVisualizerProps> = ({ amplitude, isActive, status }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let time = 0;

    const draw = () => {
      time += 0.05;
      const width = canvas.width;
      const height = canvas.height;
      const centerX = width / 2;
      const centerY = height / 2;

      ctx.clearRect(0, 0, width, height);

      // Base circle
      ctx.beginPath();
      
      let radius = 60; // Increased base radius

      if (isActive && status === 'speaking') {
         // Agent speaking: Pulse smoothly
         radius = 60 + Math.sin(time * 3) * 10;
         ctx.strokeStyle = '#0ea5e9'; // Blue-500
         ctx.fillStyle = 'rgba(14, 165, 233, 0.1)'; // Blue-500 low opacity
      } else if (isActive && status === 'listening') {
         // User speaking: React to amplitude
         const ampFactor = Math.min(amplitude * 200, 80); // Scale amplitude
         radius = 60 + ampFactor;
         ctx.strokeStyle = '#22c55e'; // Green-500
         ctx.fillStyle = 'rgba(34, 197, 94, 0.1)'; // Green-500 low opacity
      } else if (status === 'thinking') {
         radius = 60 + Math.sin(time * 10) * 3; // Fast jitter
         ctx.strokeStyle = '#eab308'; // Yellow
         ctx.fillStyle = 'rgba(234, 179, 8, 0.1)';
      } else {
         // Idle
         ctx.strokeStyle = '#e2e8f0';
         ctx.fillStyle = '#f8fafc';
      }

      ctx.lineWidth = 4;
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Outer Ripples
      if (isActive && (status === 'speaking' || (status === 'listening' && amplitude > 0.05))) {
        const baseStrokeStyle = ctx.strokeStyle;
        for (let i = 1; i <= 3; i++) {
          ctx.beginPath();
          const rippleRadius = radius + (i * 20) + Math.sin(time * 2 + i) * 5;
          ctx.arc(centerX, centerY, rippleRadius, 0, Math.PI * 2);
          ctx.strokeStyle = baseStrokeStyle;
          ctx.globalAlpha = Math.max(0, 1 - (i * 0.3));
          ctx.stroke();
        }
        ctx.globalAlpha = 1.0;
      }

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => cancelAnimationFrame(animationId);
  }, [amplitude, isActive, status]);

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <canvas ref={canvasRef} width={300} height={300} style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }} />
    </div>
  );
};

export default VoiceVisualizer;